import { Optimize } from '@totem/components/ticketing/optimizeTypes';
import { TicketQueryResult } from '@totem/components/ticketing/ticketing';

/**
 * savingsPotential is always an annual figure ($/yr).
 * savingsTermPeriod is 'Month' (or legacy 'Months') for a month-count term,
 * 'Year' (or legacy 'Years') for a year-count term.
 */
export const calculateAnnualSavings = (record: Optimize): number =>
  record.savingsPotential;

/**
 * Term length in fractional years, regardless of whether savingsTerm was
 * entered as months or years.
 */
export const calculateTermYears = (record: Optimize): number => {
  const period = record.savingsTermPeriod;
  if (period === 'Month' || period === 'Months') {
    return record.savingsTerm / 12;
  }
  return record.savingsTerm;
};

/**
 * Total projected savings over the full term (annualSavings × termYears).
 */
export const calculateTotalSavings = (record: Optimize): number =>
  calculateAnnualSavings(record) * calculateTermYears(record);

export const calculateOptimizeROIValue = (record: Optimize) => {
  const savings = calculateTotalSavings(record);
  const cost = record.requiredInvestment;
  return savings - cost;
};

export const calculateOptimizeROIPercent = (record: Optimize) => {
  const savings = calculateTotalSavings(record);
  const cost = record.requiredInvestment;
  if (cost === 0) {
    return 100;
  }
  return ((savings - cost) / cost) * 100;
};

/**
 * Returns payback in months, or null when there is no investment required.
 */
export const calculatePaybackMonths = (record: Optimize): number | null => {
  if (record.requiredInvestment <= 0) {
    return null;
  }

  const annualSavings = calculateAnnualSavings(record);

  if (annualSavings <= 0) {
    return null;
  }

  return (record.requiredInvestment / annualSavings) * 12;
};

/**
 * Human-readable payback label.
 * null → 'Immediate' (no investment); otherwise shows months or years.
 */
export const formatPaybackPeriod = (months: number | null): string => {
  if (months === null) {
    return 'Immediate';
  }

  if (months < 1) {
    return '< 1 mo';
  }

  if (months < 12) {
    return `${Math.round(months)} mo`;
  }

  const years = months / 12;

  if (years < 2) {
    return `${years.toFixed(1)} yrs`;
  }

  return `${Math.round(years)} yrs`;
};

/**
 * Monthly savings rate, regardless of whether the period is months or years.
 */
const monthlyRate = (record: Optimize): number => record.savingsPotential / 12;

/**
 * Total term length in months.
 */
export const termInMonths = (record: Optimize): number => {
  const period = record.savingsTermPeriod;
  if (period === 'Year' || period === 'Years') {
    return record.savingsTerm * 12;
  }
  return record.savingsTerm;
};

/**
 * Savings realised since the opportunity was implemented (RESOLVED/CLOSED).
 * Pass the implementation date — closedAt for CLOSED tickets, updatedAt for
 * RESOLVED tickets (best available proxy until a dedicated resolvedAt field
 * is added to the data model).
 * Capped at the full term — never exceeds total projected savings.
 */
export const calculateSavingsToDate = (
  record: Optimize,
  implementedAt: string,
): number => {
  const totalMonths = termInMonths(record);
  const implemented = new Date(implementedAt);
  const now = new Date();
  const elapsed =
    (now.getFullYear() - implemented.getFullYear()) * 12 +
    (now.getMonth() - implemented.getMonth());

  return monthlyRate(record) * Math.min(elapsed, totalMonths);
};

/**
 * Savings still remaining in the term from the implementation date
 * (0 once the term has elapsed).
 */
export const calculateSavingsRemaining = (
  record: Optimize,
  implementedAt: string,
): number => {
  const totalMonths = termInMonths(record);
  const implemented = new Date(implementedAt);
  const now = new Date();
  const elapsed =
    (now.getFullYear() - implemented.getFullYear()) * 12 +
    (now.getMonth() - implemented.getMonth());
  const remaining = Math.max(totalMonths - elapsed, 0);

  return monthlyRate(record) * remaining;
};

/**
 * Returns the date from which savings should be calculated for a
 * RESOLVED or CLOSED ticket, or null if the ticket is still open
 * (no savings are realised yet for open/approved-but-not-done tickets).
 *
 * Priority:
 *  1. implementationDate on extras.optimize — explicit date set by the user;
 *     most accurate regardless of ticket status.
 *  2. CLOSED + subStatus CANCELLED → null (work was never done).
 *  3. CLOSED → closedAt.
 *  4. RESOLVED → resolvedAt.
 *  5. Everything else (OPEN, etc.) → null.
 */
export const getImplementedAt = (
  status: string,
  subStatus: string,
  implementationDate: string | undefined,
  closedAt: string,
  resolvedAt: string,
): string | null => {
  if (implementationDate) {
    return implementationDate;
  }

  if (status === 'CLOSED') {
    return subStatus === 'CANCELLED' ? null : closedAt || null;
  }

  if (status === 'RESOLVED') {
    return resolvedAt || null;
  }

  return null;
};

/**
 * Returns false for cancelled opportunities — work was never done and they
 * should be hidden from all dashboard views.
 *
 * Checks opportunityStatus first (new model). Falls back to the legacy
 * ticket.status + subStatus check for records that predate the new field.
 */
export const isVisibleOpportunity = (rec: TicketQueryResult): boolean => {
  const opportunityStatus = rec.ticket.extras?.optimize?.opportunityStatus;

  if (opportunityStatus) {
    return opportunityStatus !== 'Cancelled';
  }

  // Legacy fallback for tickets without opportunityStatus set
  return !(
    rec.ticket.status === 'CLOSED' && rec.ticket.subStatus === 'CANCELLED'
  );
};

/**
 * Short human-readable timeframe label, e.g. "12 mo" or "3 yrs".
 */
export const formatSavingsTimeframe = (record: Optimize): string => {
  const period =
    record.savingsTermPeriod === 'Month' ||
    record.savingsTermPeriod === 'Months'
      ? 'mo'
      : 'yr';
  return `${record.savingsTerm} ${period}`;
};
